<template>
    <common-page flex-column card-content>
        <query-view ref="queryView"
                    config-code="em_arch_fixed_asset"
                    disable-row-check
                    with-custom-actions-head-of-row
                    custom-columns="deptMismatch,costCenterCode">
            <template #leftHeader>
                <mini-button v-if="finance" name="add" @btnClick="openEdit(null)">
                    <i class="fa-solid fa-plus"></i>
                    <span class="ml-1">{{ $t('label.em.asset.emArchFixedAsset.add') }}</span>
                </mini-button>
                <mini-button v-if="importable" name="import" class="ml-1" @btnClick="openImport">
                    <i class="fa-solid fa-file-import"></i>
                    <span class="ml-1">{{ $t('label.em.asset.emArchFixedAsset.import') }}</span>
                </mini-button>
                <mini-button v-if="finance" name="financeExport" class="ml-1"
                             :loading="exporting" @btnClick="exportFinance">
                    <i class="fa fa-fw fa-file-excel-o"></i>
                    <span class="ml-1">{{ $t('label.em.asset.emArchFixedAsset.financeExport') }}</span>
                </mini-button>
            </template>
            <template #headOfRow="{row}">
                <mini-button v-if="finance" name="edit" @btnClick="openEdit(row)">
                    <i class="fa-solid fa-pen-to-square"></i>
                </mini-button>
                <mini-button name="history" class="ml-1"
                             :title="$t('label.em.asset.emArchFixedAsset.history')"
                             @btnClick="openHistory(row)">
                    <i class="fa fa-fw fa-history"></i>
                </mini-button>
                <!-- 所有人可见的只读附件入口：非财务打不开编辑弹窗，原先完全看不到档案附件 -->
                <mini-button name="attachments" class="ml-1"
                             :title="$t('label.em.asset.emArchFixedAsset.attachments')"
                             @btnClick="openAttachments(row)">
                    <i class="fa fa-fw fa-paperclip"></i>
                </mini-button>
            </template>
            <template #custom(costCenterCode)="{row}">
                {{ costCenterText(row.costCenterCode, row.costCenterName) }}
            </template>
            <template #custom(deptMismatch)="{row}">
                <span v-if="isDeptMismatch(row)" class="text-danger"
                      :title="$t('label.em.asset.emArchFixedAsset.deptMismatchTip')">
                    <i class="fa fa-fw fa-exclamation-triangle"></i>
                </span>
            </template>
        </query-view>

        <template #outer>
            <em-arch-fixed-asset-edit-modal ref="editModal" :finance="finance"
                                            @saved="refreshTable"></em-arch-fixed-asset-edit-modal>
            <em-arch-fixed-asset-import-modal ref="importModal"
                                              @imported="refreshTable"></em-arch-fixed-asset-import-modal>
            <em-arch-fixed-asset-history-modal ref="historyModal"></em-arch-fixed-asset-history-modal>
            <re-b-modal ref="attachmentModal" hide-footer
                        :header="attachmentHeader">
                <div v-if="attachmentLoading" class="text-center text-muted py-3">
                    <i class="fa fa-fw fa-spinner fa-spin"></i>
                </div>
                <div v-else-if="!attachmentIds" class="text-center text-muted py-3">
                    {{ $t('label.em.asset.emArchFixedAsset.noAttachment') }}
                </div>
                <!-- 不传 is-editing 即只读：只列文件名与下载链接 -->
                <em-attachment v-else :key="attachmentKey"
                               get-items-by-self
                               :attachment-ids="attachmentIds"></em-attachment>
            </re-b-modal>
        </template>
    </common-page>
</template>

<script>
import CommonPage from "@/components/echarts-part/stuff/CommonPage.vue";
import QueryView from "@/components/common/display/query-view/QueryView.vue";
import MiniButton from "@/components/common/buttons/MiniButton.vue";
import EmArchFixedAssetEditModal from "@/views/em/asset/EmArchFixedAssetEditModal.vue";
import EmArchFixedAssetImportModal from "@/views/em/asset/EmArchFixedAssetImportModal.vue";
import EmArchFixedAssetHistoryModal from "@/views/em/asset/EmArchFixedAssetHistoryModal.vue";
import emCommonApiMixin from "@/mixin/em/common/api";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import {costCenterText} from "@/views/em/asset/costCenterText";
import EmAttachment from "@/components/em/EmAttachment";

/**
 * 固定资产档案列表（验收 #1 / #2 UI / #3 UI / #6 UI）
 * <p>
 * 列表走 QueryView 配置 em_arch_fixed_asset（service = em-archive）；
 * 原值 / 已提折旧 / 账面价值 / 折旧年限 / 折旧年限-月 这些财务字段有意不在配置里（qg 通用查询绕过 service 层无法脱敏），
 * 财务人员在编辑弹窗中查看与维护。
 * 附件同样不在配置里：行内「附件」按钮对所有人开放，走详情接口取附件 id（详情接口只脱敏财务字段，不脱敏附件）。
 */
export default {
    name: "EmArchFixedAssetList",
    components: {
        EmAttachment,
        ReBModal,
        EmArchFixedAssetHistoryModal,
        EmArchFixedAssetImportModal,
        EmArchFixedAssetEditModal,
        MiniButton,
        QueryView,
        CommonPage
    },
    mixins: [emCommonApiMixin, emCommonUtilMixin],
    data() {
        return {
            finance: false,
            // 导入（含模板下载）额外放行系统管理员，与其余财务写操作分开判断
            importable: false,
            exporting: false,
            attachmentLoading: false,
            attachmentAsset: null,
            attachmentIds: '',
            // 强制重建 em-attachment：它的附件列表活在实例上，换档案时不重建会残留上一条的附件
            attachmentKey: 0,
        }
    },
    computed: {
        attachmentHeader() {
            let title = this.$t('title.em.asset.emArchFixedAsset.attachments');
            let asset = this.attachmentAsset;
            return asset ? `${title} - ${asset.assetNo || ''} / ${asset.subAssetNo || ''}` : title;
        },
    },
    mounted() {
        this.loadPermission();
    },
    methods: {
        costCenterText,

        loadPermission() {
            // api/get 拿到的就是 body（EmResponseUtils.success 裸透传）
            this.$_emApiMixin_get(process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/permission')
                    .then(res => {
                        this.finance = !!(res && res.finance);
                        this.importable = !!(res && res.importable);
                    });
        },

        openEdit(row) {
            this.$refs.editModal.open(row);
        },

        openImport() {
            this.$refs.importModal.open();
        },

        openHistory(row) {
            this.$refs.historyModal.open(row);
        },

        /**
         * 只读附件弹窗：现拉详情取附件 id，保证看到的是库里最新的挂接结果（而不是列表行的旧快照）
         */
        openAttachments(row) {
            if (!row || !row.id) {
                return;
            }
            this.attachmentAsset = row;
            this.attachmentIds = '';
            this.attachmentKey++;
            this.attachmentLoading = true;
            this.$refs.attachmentModal.showModal();
            // api/get 拿到的就是 body（EmResponseUtils.success 裸透传）
            this.$_emApiMixin_get(`${process.env.VUE_APP_EM_ARCHIVE_URL}/emArchFixedAsset/detail/${row.id}`)
                    .then(res => {
                        this.attachmentIds = (res && res.attachments) || '';
                        this.attachmentKey++;
                    })
                    .finally(() => this.attachmentLoading = false);
        },

        /**
         * 财务导出：原值 / 已提折旧 / 账面价值 / 折旧年限 / 折旧年限-月 有意不在 QueryView 配置里，
         * 这个接口是它们唯一的批量出口，后端同样只放行财务人员。
         * 列表未开行勾选，这里导出全部（body 传空数组）。
         */
        exportFinance() {
            this.exporting = true;
            this.downloadWithHeader(
                    process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset/export',
                    this.$t('label.em.asset.emArchFixedAsset.exportFileName'),
                    (success, errorMsg) => {
                        this.exporting = false;
                        if (!success) {
                            this.$bvToast.toast(
                                    errorMsg || this.$t('label.em.asset.emArchFixedAsset.downloadFailed'), {
                                        variant: 'danger',
                                        noCloseButton: true,
                                        autoHideDelay: 4000
                                    });
                        }
                    },
                    'POST',
                    []);
        },

        isDeptMismatch(row) {
            return Number(row.deptMismatch) === 1;
        },

        refreshTable() {
            let queryView = this.$refs.queryView;
            if (queryView) {
                queryView.queryRows();
                queryView.countRows();
            }
        },
    }
}
</script>

<style scoped>
</style>
